import '../orchestrators/onPreCoprincipalAccountRemovedOrchestrator';
import '../orchestrators/selectFolderOrchestrator';

import {
    getCoprincipalAccounts,
    getGlobalSettingsAccountMailboxInfo,
    StartupFilter,
    ContractsSupportedFilter,
} from 'owa-account-source-list-store';
import { loadFavorites } from 'owa-favorites';
import {
    lazyLoadUnifiedGroupsSettings,
    lazyUpdateFolderRulePermissionAllGroup,
} from 'owa-groups-shared-actions';
import { type FolderForestNodeType } from 'owa-favorites-types';
import type { MailFolder } from 'owa-graph-schema';
import { PerformanceDatapoint } from 'owa-analytics';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import folderStore from 'owa-folders';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { isConsumer } from 'owa-session-store';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isGroupsEnabled } from 'owa-account-capabilities/lib/isGroupsEnabled';
import { lazyEnsureAllAdditionalAccountInfoData } from 'owa-account-source-list/lib/lazyFunctions';
import { lazyLoadGroupAccountSourcesForCurrentSession } from 'owa-group-mail-left-nav-actions';
import { loadMailFolders } from 'owa-mail-folder-store';
import { markFunction } from 'owa-performance';
import { checkAttachmentsSearchFolderAndLoad } from '../utils/checkAttachmentsSearchFolderAndLoad';
import selectDefaultFolder from './selectDefaultFolder';

/**
 * Load the folder forest to stores, i.e. folders and favorites
 */

const defaultExport: (sessionData?: SessionData | undefined) => Promise<void> = markFunction(
    async function loadFolderForest(sessionData?: SessionData) {
        const mailboxInfo = getGlobalSettingsAccountMailboxInfo();
        const datapoint = new PerformanceDatapoint('LoadFolderForest', mailboxInfo);

        await loadMailFolders(mailboxInfo, sessionData);

        if (!isGlobalFeatureEnabled('fp-othermailboxconfig-cache')) {
            await lazyEnsureAllAdditionalAccountInfoData.importAndExecute();
        }

        loadFavorites(mailboxInfo);

        // If the selected folder is invalid
        if (!isSelectedNodeDataValid()) {
            selectDefaultFolder('ResetInbox', sessionData);
        }

        if (isGroupsEnabled(mailboxInfo) && !isGlobalFeatureEnabled('grp-disable-loadgroups')) {
            datapoint.addCustomData({ isSourceIdAwareGroupStore: true });
            loadGroupsFolderForest();
        }
        checkAttachmentsSearchFolderAndLoad();
        datapoint.end();
    },
    'ff'
);
export default defaultExport;

function isSelectedNodeDataValid(): boolean {
    const selectedNode = getSelectedNode();
    switch (selectedNode.type) {
        case 0:
        case 1:
            const selectedFolderId = selectedNode.id;
            const folder: MailFolder | undefined = selectedFolderId
                ? folderStore().folderTable.get(selectedFolderId)
                : undefined;
            return folder !== undefined;

        case 2:
            return true;

        default:
            break;
    }

    return false;
}

async function loadGroupsFolderForest() {
    const accounts = getCoprincipalAccounts(
        StartupFilter.StartingOrCompleteOrError,
        ContractsSupportedFilter.Any
    );
    for (const acct of accounts) {
        if (acct && isGroupsEnabled(acct.mailboxInfo)) {
            const mailboxInfo = acct.mailboxInfo;
            await lazyLoadGroupAccountSourcesForCurrentSession.importAndExecute(acct);

            await lazyLoadUnifiedGroupsSettings.importAndExecute(mailboxInfo);

            if (isGlobalFeatureEnabled('grp-loadFolders') && !isConsumer(undefined, mailboxInfo)) {
                lazyUpdateFolderRulePermissionAllGroup.importAndExecute(mailboxInfo);
            }
        }
    }
}
